import '~/map/createNew/createNewSalesRepMapSuccess.component.autoload';
import {
  type FC, useCallback, useEffect, useMemo,
} from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { type UploadStatus } from '~/_shared/components/uploadStatus/uploadStatus.component';
import { useShowWalkthroughModal } from '~/_shared/components/walkthroughModal/useShowWalkthroughModal';
import { useTranslation } from '~/_shared/utils/hooks';
import { createMapLimitReachedAppError } from '~/appError/specificErrors/mapLimitReached/mapLimitReached.helpers';
import { MapListSortType } from '~/map/listing/mapListing.helpers';
import { useMapListingItems } from '~/map/listing/useMapListingItems';
import { type ModalProps } from '~/modal/modalType.enum';
import { useModal } from '~/modal/useModal.hook';
import { mapListingMapCreated } from '~/store/frontendState/mapListing/mapListing.actionCreators';
import { closeAllModals } from '~/store/modal/modal.actionCreators';
import { useClientIdSelector } from '~/store/selectors/useClientIdSelector';
import {
  useClientIsNew, useUserIdSelector,
} from '~/store/userData/userData.selectors';
import {
  getMapUrl, MAP_TEMPLATES_MODAL_OPEN_URL_PARAM,
} from '../map.helpers';
import { MAP_LIMIT_REACHED_MESSAGE } from '../map.repository';
import { CreateNewMapComponent } from './createNewMap.component';
import { useCreateBlankMap } from './useCreateBlankMap';
import { useCreateMapFromInput } from './useCreateMapFromInput';
import { useCreateMapFromSpreadsheet } from './useCreateMapFromSpreadsheet';

export type CreateNewMapContainerProps = ModalProps<{
  prefillFormData?: {[key: string]: string | boolean};
  isNewSalesRepMap?: boolean;
}>;

export const CreateNewMapContainer: FC<CreateNewMapContainerProps> = (props) => {
  const dispatch = useDispatch();
  const clientId = useClientIdSelector();
  const userId = useUserIdSelector();
  const navigate = useNavigate();
  const [t] = useTranslation();
  const { replaceCurrentModal: closeCurrentModalAndOpenGoogleSheetMapCreate } = useModal('CreateGooogleSheetMap');
  const { openModal: onCreateNewSalesRepMapSuccessModal } = useModal('CreateNewSalesRepMapSuccess');
  const { replaceCurrentModal: onOpenLayerMapsModal } = useModal('CreateLayeredMap');
  const { showWalkthroughModal } = useShowWalkthroughModal();
  const { mapListingItems, isLoading: isMapListingLoading } = useMapListingItems({
    isActive: true,
    searchQuery: '',
    sortType: MapListSortType.Created_DESC,
  });
  const isFirstMap = useClientIsNew();

  const {
    createBlankMap,
    resetState: resetBlankMapState,
    errorMessage: blankMapErrorMessage,
    isLoading: isBlankMapLoading,
  } = useCreateBlankMap(clientId);

  const {
    resetState: resetSpreadsheetMapState,
    isLoading: isSpreadsheetMapLoading,
    errorMessage: spreadsheetMapErrorMessage,
    clearErrorMessage: closeSpreadsheetMapErrorMessage,
    createMapFromSpreadsheet,
  } = useCreateMapFromSpreadsheet(clientId, userId);

  const createMapFromInputProps = useCreateMapFromInput(clientId, userId);

  const onStartGoogleSheetsClick = useCallback((mapName: string, mapDescription: string) =>
    closeCurrentModalAndOpenGoogleSheetMapCreate(({
      initialMapInfo: {
        name: mapName,
        description: mapDescription,
      },
    })), [closeCurrentModalAndOpenGoogleSheetMapCreate]);

  const onCreateMapSuccess = useCallback((mapId: number) => {
    props.onClose();
    showWalkthroughModal();

    if (props.isNewSalesRepMap) {
      onCreateNewSalesRepMapSuccessModal({
        mapUrl: getMapUrl(mapId),
      });
    }
    else {
      navigate(getMapUrl(mapId, isFirstMap ? { [MAP_TEMPLATES_MODAL_OPEN_URL_PARAM]: 'true' } : undefined));
    }

    dispatch(mapListingMapCreated());
  }, [dispatch, isFirstMap, navigate, onCreateNewSalesRepMapSuccessModal, props, showWalkthroughModal]);

  const onCreateBlankMap = useCallback((mapName: string, mapDescription: string) =>
    createBlankMap(mapName, mapDescription, onCreateMapSuccess),
  [createBlankMap, onCreateMapSuccess]);

  const onCreateMapFromSpreadsheet = useCallback((
    file: File,
    mapName: string,
    mapDescription: string,
    onProgressChange: (progress: number) => void,
    onStatusChange: (status: UploadStatus) => void
  ) => createMapFromSpreadsheet(file, mapName, mapDescription, onProgressChange, onStatusChange, onCreateMapSuccess),
  [createMapFromSpreadsheet, onCreateMapSuccess]);

  const layeringAvailability = useMemo(() => ({
    isDisabled: mapListingItems.length < 2 || isMapListingLoading,
    isLoading: isMapListingLoading,
  }), [isMapListingLoading, mapListingItems.length]);

  useEffect(() => {
    if (!props.isOpen) {
      resetBlankMapState();
      resetSpreadsheetMapState();
    }
  }, [props.isOpen, resetBlankMapState, resetSpreadsheetMapState]);

  useEffect(() => {
    if (!isBlankMapLoading) {
      return;
    }

    resetSpreadsheetMapState();
  }, [isBlankMapLoading, resetSpreadsheetMapState]);

  useEffect(() => {
    if (!isSpreadsheetMapLoading) {
      return;
    }

    resetBlankMapState();
  }, [isSpreadsheetMapLoading, resetBlankMapState]);

  useEffect(() => {
    if (blankMapErrorMessage === MAP_LIMIT_REACHED_MESSAGE || spreadsheetMapErrorMessage === MAP_LIMIT_REACHED_MESSAGE) {
      dispatch(closeAllModals());
      dispatch(createMapLimitReachedAppError(t));
    }
  }, [blankMapErrorMessage, dispatch, spreadsheetMapErrorMessage, t]);

  if (clientId === null || userId === null) {
    return null;
  }

  return (
    <CreateNewMapComponent
      createBlankMapErrorMessage={blankMapErrorMessage}
      createMapFromInputProps={createMapFromInputProps}
      createSpreadsheetMapErrorMessage={spreadsheetMapErrorMessage}
      layeringAvailability={layeringAvailability}
      isLoading={isSpreadsheetMapLoading || isBlankMapLoading || createMapFromInputProps.isLoading}
      isNewSalesRepMap={props.isNewSalesRepMap}
      isOpen={props.isOpen}
      onClose={props.onClose}
      onCreateBlankMap={onCreateBlankMap}
      onCreateMapFromSpreadsheet={onCreateMapFromSpreadsheet}
      onCreateMapSuccess={onCreateMapSuccess}
      onCreateSpreadsheetMapErrorMessageClose={closeSpreadsheetMapErrorMessage}
      onOpenLayerMapsModal={onOpenLayerMapsModal}
      onStartGoogleSheetsClick={onStartGoogleSheetsClick}
      prefillFormData={props.prefillFormData}
    />
  );
};
